import type {
    ContentDefinition,
    ContentOutput,
    ForDefinition,
    ForOutput,
    ImportBinding,
    PluginDefinition,
    PluginOutput,
    UseBinding,
} from "@magic-md/core";
import type { RuntimeUnitInfo } from "@magic-md/plugin-sdk";
import type {
    ContentExecutionEntry,
    ForExecutionEntry,
    ImportExecutionEntry,
    UseExecutionEntry,
} from "../store/plugin-execution-store";

export function attachDefinitionOutputs(
    definitions: Map<string, PluginDefinition>,
    outputs?: Map<string, PluginOutput>,
): Map<string, PluginDefinition> {
    if (!outputs || outputs.size === 0) return definitions;
    const next = new Map<string, PluginDefinition>();
    for (const [id, definition] of definitions) {
        const output = outputs.get(id) ?? definition.output;
        next.set(
            id,
            output === definition.output ? definition : { ...definition, output },
        );
    }
    return next;
}

export interface BuildConfigPanelUnitsInput {
    definitions: Map<string, PluginDefinition>;
    definitionOutputs?: Map<string, PluginOutput>;
    forDefinitions?: Map<string, ForDefinition>;
    contentDefinitions?: Map<string, ContentDefinition>;
    imports?: ImportBinding[];
    uses?: UseBinding[];
    forExecutions?: Map<string, ForExecutionEntry>;
    contentExecutions?: Map<string, ContentExecutionEntry>;
    importExecutions?: Map<string, ImportExecutionEntry>;
    useExecutions?: Map<string, UseExecutionEntry>;
}

export function buildConfigPanelUnits({
    definitions,
    definitionOutputs,
    forDefinitions,
    contentDefinitions,
    imports,
    uses,
    forExecutions,
    contentExecutions,
    importExecutions,
    useExecutions,
}: BuildConfigPanelUnitsInput): RuntimeUnitInfo[] {
    const units = new Map<string, RuntimeUnitInfo>();

    for (const [id, definition] of definitions) {
        const output = definitionOutputs?.get(id) ?? definition.output;
        units.set(id, {
            id,
            kind: "definition",
            pluginName: definition.pluginName,
            hasOutput: output !== undefined,
            output,
            config: definition.config,
            attributes: compactAttributes({
                outputMode: definition.outputMode,
                template: definition.template,
                params: definition.params,
            }),
        });
    }

    for (const [id, forDefinition] of forDefinitions ?? []) {
        const output = buildForOutputSample(forDefinition, forExecutions?.get(id));
        units.set(id, {
            id,
            kind: "for",
            hasOutput: output !== undefined,
            output,
            attributes: compactAttributes({
                each: forDefinition.each,
                of: forDefinition.of,
                limit: forDefinition.limit,
                offset: forDefinition.offset,
                concurrency: forDefinition.concurrency,
                outputMode: forDefinition.outputMode,
                template: forDefinition.template,
                params: forDefinition.params,
            }),
        });
    }

    for (const [id, contentDefinition] of contentDefinitions ?? []) {
        const output = contentExecutions?.get(id)?.output ?? contentDefinition.output;
        units.set(id, {
            id,
            kind: "content",
            hasOutput: output !== undefined,
            output,
            attributes: compactAttributes({
                template: contentDefinition.template,
                params: contentDefinition.params,
                outputMode: contentDefinition.outputMode,
            }),
        });
    }

    for (const binding of imports ?? []) {
        const id = binding.namespace;
        const execution = importExecutions?.get(id);
        const output = binding.output ?? buildImportOutputSample(execution);
        units.set(id, {
            id,
            kind: "import",
            hasOutput: output !== undefined,
            output,
            attributes: compactAttributes({
                source: binding.source,
                namespace: binding.namespace,
                pick: binding.pick,
                exclude: binding.exclude,
                rename: binding.rename,
                document: binding.document,
                line: binding.line,
                outputMode: binding.outputMode,
            }),
        });
    }

    for (const binding of uses ?? []) {
        const execution = useExecutions?.get(binding.id);
        const existing = units.get(binding.id);
        const output = buildUseOutputSample(binding, execution) ?? existing?.output;
        units.set(binding.id, {
            id: binding.id,
            kind: "use",
            hasOutput: output !== undefined,
            output,
            attributes: compactAttributes({
                source: binding.source,
                with: binding.with,
                patch: binding.patch,
                bind: binding.bind,
                outputMode: binding.outputMode,
                template: binding.template,
                params: binding.params,
            }),
        });
    }

    return [...units.values()].sort((a, b) => a.id.localeCompare(b.id));
}

function compactAttributes(
    attributes: Record<string, unknown>,
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(attributes).filter(([, value]) => value !== undefined),
    );
}

function buildForOutputSample(
    definition: ForDefinition,
    execution?: ForExecutionEntry,
): ForOutput | undefined {
    if (definition.output) return definition.output;
    if (!execution || execution.iterations.length === 0) return undefined;
    return {
        iterations: execution.iterations.map((iteration) => ({
            index: iteration.index,
            item: iteration.item,
            outputs: iteration.outputs as Record<string, PluginOutput>,
            definitions: iteration.definitions,
            contentDefinitions: iteration.contentDefinitions,
            success: iteration.success,
            error: iteration.error,
        })),
    };
}

function buildImportOutputSample(
    execution: ImportExecutionEntry | undefined,
): unknown {
    if (!execution?.symbols && !execution?.document) return undefined;
    return {
        symbols: execution.symbols ?? [],
        ...(execution.document ? { document: execution.document } : {}),
    };
}

function buildUseOutputSample(
    binding: UseBinding,
    execution: UseExecutionEntry | undefined,
): PluginOutput | ContentOutput | ForOutput | Record<string, unknown> | undefined {
    if (binding.output?.nodeOutput) return binding.output.nodeOutput;
    if (binding.output?.contentOutput) return binding.output.contentOutput;
    if (binding.output?.forOutput) return binding.output.forOutput;
    if (!execution?.materializedBody) return undefined;
    return { materializedBody: execution.materializedBody };
}
